import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, Eye } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Footer from './Footer';

const NewsPage = () => {
  const { t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/news`);
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('el-GR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (selectedArticle) {
    // Generate BlogPosting schema based on article title
    const getBlogPostingSchema = (article) => {
      const baseSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": article.title,
        "description": article.excerpt,
        "url": `https://ai-agency.gr/insights/${article.id}`,
        "datePublished": article.date,
        "dateModified": article.date,
        "author": {
          "@type": "Person",
          "name": article.author || "Grigorios Tsinaforniotis",
          "jobTitle": "CEO",
          "url": "https://www.linkedin.com/in/grigorios-tsinaforniotis/",
          "affiliation": {
            "@type": "Organization",
            "name": "PROTOS AI Agency"
          }
        },
        "publisher": {
          "@type": "Organization",
          "name": "PROTOS AI Agency",
          "url": "https://ai-agency.gr",
          "logo": {
            "@type": "ImageObject",
            "url": "https://ai-agency.gr/logo.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://ai-agency.gr/insights/${article.id}`
        },
        "image": article.image || article.imageUrl,
        "inLanguage": "en",
        "isPartOf": {
          "@type": "Blog",
          "name": "PROTOS AI Agency Insights",
          "url": "https://ai-agency.gr/insights"
        }
      };

      // Add specific keywords and about based on article title
      if (article.title.includes("AI Act Compliance") || article.title.includes("NOMOKRATIA")) {
        baseSchema.keywords = ["EU AI Act compliance", "legal AI Europe", "RAG legal AI", "AI Act risk classification", "regulatory AI", "NOMOKRATIA.AI", "AI compliance software EU"];
        baseSchema.about = [
          {"@type": "Thing", "name": "EU AI Act", "sameAs": "https://eur-lex.europa.eu/eli/reg/2024/1689"},
          {"@type": "Thing", "name": "Retrieval-Augmented Generation"},
          {"@type": "Thing", "name": "Legal AI"}
        ];
      } else if (article.title.includes("DSA Chatbot")) {
        baseSchema.keywords = ["DSA chatbot", "Digital Services Act AI", "EU regulation chatbot", "DSA compliance tool", "legal chatbot European Commission", "regulatory conversational AI"];
        baseSchema.about = [
          {"@type": "Thing", "name": "Digital Services Act", "sameAs": "https://eur-lex.europa.eu/eli/reg/2022/2065"},
          {"@type": "Thing", "name": "EU AI Act", "sameAs": "https://eur-lex.europa.eu/eli/reg/2024/1689"},
          {"@type": "Thing", "name": "Conversational AI"}
        ];
      } else if (article.title.includes("NIS2") || article.title.includes("CyberSentinel")) {
        baseSchema.keywords = ["NIS2 compliance AI", "NIS2 automation", "cybersecurity AI Europe", "CyberSentinel", "AI SOC automation", "NIS2 incident reporting AI", "EU cybersecurity compliance"];
        baseSchema.about = [
          {"@type": "Thing", "name": "NIS2 Directive", "sameAs": "https://eur-lex.europa.eu/eli/dir/2022/2555"},
          {"@type": "Thing", "name": "Cybersecurity"},
          {"@type": "Thing", "name": "Threat Intelligence"}
        ];
      } else if (article.title.includes("PalaioScript") || article.title.includes("Ancient Manuscripts") || article.title.includes("Mount Athos")) {
        baseSchema.keywords = ["AI manuscript transcription", "ancient manuscript OCR", "PalaioScript AI", "Mount Athos AI", "digital heritage AI", "handwritten text recognition", "Byzantine manuscript AI"];
        baseSchema.about = [
          {"@type": "Thing", "name": "Manuscript Digitisation"},
          {"@type": "Thing", "name": "Computer Vision"},
          {"@type": "Thing", "name": "Cultural Heritage"},
          {"@type": "Place", "name": "Mount Athos", "sameAs": "https://www.wikidata.org/wiki/Q133855"}
        ];
      } else if (article.title.includes("Mistral") || article.title.includes("Digital Sovereignty")) {
        baseSchema.keywords = ["European digital sovereignty AI", "Mistral vs GPT", "sovereign AI Europe", "EU AI infrastructure", "GAIA-X AI", "European AI models", "Mistral legal AI", "GDPR AI compliance"];
        baseSchema.about = [
          {"@type": "Thing", "name": "Digital Sovereignty"},
          {"@type": "Thing", "name": "Mistral AI", "sameAs": "https://www.wikidata.org/wiki/Q124488953"},
          {"@type": "Thing", "name": "GDPR", "sameAs": "https://eur-lex.europa.eu/eli/reg/2016/679"}
        ];
      }

      return baseSchema;
    };

    // Einzelartikel-Ansicht
    return (
      <div className="min-h-screen tech-bg-primary">
        <Helmet>
          <title>{selectedArticle.title} | PROTOS AI Agency Insights</title>
          <meta name="description" content={selectedArticle.excerpt} />
          <link rel="canonical" href={`https://ai-agency.gr/insights/${selectedArticle.id}`} />
          <meta name="robots" content="index, follow" />
          <meta property="og:title" content={selectedArticle.title} />
          <meta property="og:description" content={selectedArticle.excerpt} />
          <meta property="og:image" content={selectedArticle.image || selectedArticle.imageUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={`https://ai-agency.gr/insights/${selectedArticle.id}`} />
          
          {/* BlogPosting Schema */}
          <script type="application/ld+json">
            {JSON.stringify(getBlogPostingSchema(selectedArticle))}
          </script>
        </Helmet>
        <div className="tech-grid-bg fixed inset-0 pointer-events-none z-0"></div>
        
        {/* Header */}
        <header className="tech-header sticky top-0 z-50 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-3">
                <a href="/" className="cursor-pointer hover:opacity-80 transition-opacity duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_globalized-site/artifacts/nb42ugut_PROTOS%20AI%20AGency.png" 
                    alt="Protos AI Agency Logo"
                    className="w-32 h-20 md:w-48 md:h-28 object-contain"
                  />
                </a>
              </div>
              <button
                onClick={() => setSelectedArticle(null)}
                className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Επιστροφή στα Νέα</span>
              </button>
            </div>
          </div>
        </header>

        {/* Artikel Content */}
        <main className="relative z-10 pt-8 pb-20">
          <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-blue-500/20">
              {/* Artikel Header */}
              <header className="mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                  {selectedArticle.title}
                </h1>
                <div className="flex items-center space-x-4 text-gray-400 mb-6">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(selectedArticle.publishedAt)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Eye className="w-4 h-4" />
                    <span>Νέα ανάρτηση</span>
                  </div>
                </div>
                
                {/* Artikel Hauptbild */}
                <div className="aspect-video rounded-xl overflow-hidden mb-8">
                  <img 
                    src={selectedArticle.image || selectedArticle.imageUrl} 
                    alt={selectedArticle.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 9'%3E%3Crect width='16' height='9' fill='%23374151'/%3E%3Ctext x='8' y='4.5' font-family='Arial' font-size='1.2' fill='%23ffffff' text-anchor='middle' dominant-baseline='middle'%3EΕικόνα Άρθρου%3C/text%3E%3C/svg%3E";
                    }}
                  />
                </div>
              </header>

              {/* Artikel Body */}
              <div className="prose prose-invert prose-lg max-w-none">
                <div 
                  className="text-gray-300 leading-relaxed space-y-6"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content.replace(/\n/g, '<br />') }}
                />
              </div>
            </div>
          </article>
        </main>
      </div>
    );
  }

  // News-Liste
  return (
    <div className="min-h-screen tech-bg-primary">
      <Helmet>
        <title>Νέα & Αναφορές | Protos AI Agency - Τελευταίες Εξελίξεις στην Τεχνητή Νοημοσύνη</title>
        <meta name="description" content="Παρακολουθήστε τις τελευταίες εξελίξεις, αναφορές και νέα από το Protos AI Agency. Ενημερωθείτε για καινοτομίες στην τεχνητή νοημοσύνη για δημόσιο και εκκλησία." />
        <link rel="canonical" href="https://ai-agency.gr/news" />
        <meta property="og:title" content="Νέα & Αναφορές | Protos AI Agency" />
        <meta property="og:description" content="Τελευταίες εξελίξεις και αναφορές από το Protos AI Agency στον τομέα της τεχνητής νοημοσύνης." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ai-agency.gr/news" />
        
        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Αρχική",
                "item": "https://ai-agency.gr/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Νέα",
                "item": "https://ai-agency.gr/news"
              }
            ]
          })}
        </script>
      </Helmet>
      <div className="fixed inset-0 pointer-events-none z-0"></div>
      
      {/* Header */}
      <header className="tech-header sticky top-0 z-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <a href="/" className="cursor-pointer hover:opacity-80 transition-opacity duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_globalized-site/artifacts/nb42ugut_PROTOS%20AI%20AGency.png" 
                  alt="Protos AI Agency Logo"
                  className="w-32 h-20 md:w-48 md:h-28 object-contain"
                />
              </a>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="/" className="tech-nav-link">Αρχική</a>
              <a href="/#services" className="tech-nav-link">Υπηρεσίες</a>
              <a href="/#team" className="tech-nav-link">Ομάδα</a>
              <a href="/news" className="tech-nav-link active">Νέα</a>
              <a href="/#contact" className="tech-nav-link">Επικοινωνία</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-8 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold tech-heading mb-6 tracking-tight">
              Νέα & Αναφορές
            </h1>
            <p className="text-xl tech-text leading-relaxed max-w-3xl mx-auto">
              Παρακολουθήστε τις τελευταίες εξελίξεις και αναφορές από το Protos AI Agency
            </p>
          </div>

          {/* Articles Grid */}
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article) => (
                <article
                  key={article.id}
                  className="bg-slate-800/50 rounded-2xl overflow-hidden border border-blue-500/20 hover:border-blue-400/40 transition-all duration-300 cursor-pointer transform hover:scale-105"
                  onClick={() => setSelectedArticle(article)}
                >
                  {/* Artikel Bild */}
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={article.image || article.imageUrl} 
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      onError={(e) => {
                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 9'%3E%3Crect width='16' height='9' fill='%23374151'/%3E%3Ctext x='8' y='4.5' font-family='Arial' font-size='1.2' fill='%23ffffff' text-anchor='middle' dominant-baseline='middle'%3EΕικόνα%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center space-x-2 text-sm text-blue-400 mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(article.date || article.publishedAt)}</span>
                    </div>
                    
                    <h2 className="text-xl font-bold text-white mb-3 line-clamp-2">
                      {article.title}
                    </h2>
                    
                    <p className="text-gray-400 line-clamp-3 leading-relaxed">
                      {article.excerpt}
                    </p>
                    
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <span className="text-blue-400 hover:text-blue-300 font-medium">
                        Διαβάστε περισσότερα →
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="bg-slate-800/50 rounded-2xl p-8 border border-blue-500/20 max-w-md mx-auto">
                <h3 className="text-xl font-bold text-white mb-4">Δεν υπάρχουν νέα</h3>
                <p className="text-gray-400">
                  Τα νέα και οι αναφορές θα εμφανιστούν εδώ σύντομα.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NewsPage;